import React, { useEffect } from 'react';
import {
  Box, Typography, Stack, TextField, InputAdornment,
} from '@mui/material';

const navy = '#1e3a5f';

// ברירת מחדל — יציאה רגילה (tzeit 8.5°), ייעשה שימוש ב-fetchMoadActions
export const DEFAULT_EXIT_ZMAN = { key: 'tzeit85deg', offset: 0, icon: 'MoonStar' };

const DEFAULT_ENTRY = { key: 'sunset', minsBeforeSunset: 30, offset: 0 };

const fieldSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

/**
 * MoadCalcStep
 * הגדרת שיטת חישוב כניסת המועד — כמה דקות לפני השקיעה (או פלג המנחה).
 * יציאת המועד (צאת כוכבים 8.5°) נקבעת כברירת מחדל ואינה דורשת הגדרה מראש.
 *
 * formData.entryZman: { key, minsBeforeSunset, offset, icon }
 */
const MoadCalcStep = ({ formData, onChange }) => {
  const entry = { ...DEFAULT_ENTRY, ...formData?.entryZman };

  // ודא ש-entryZman תמיד מאותחל ב-formData גם אם המשתמש לא שינה כלום
  useEffect(() => {
    if (!formData?.entryZman) {
      onChange({ entryZman: { ...DEFAULT_ENTRY } });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setEntry = (partial) => onChange({ entryZman: { ...entry, ...partial } });

  const handleMins = (e) => {
    const raw = e.target.value;
    if (raw === '' || raw === '-') { setEntry({ minsBeforeSunset: raw }); return; }
    const n = parseInt(raw, 10);
    if (!isNaN(n) && n >= 0) setEntry({ minsBeforeSunset: n });
  };

  const handlePlagOffset = (e) => {
    const raw = e.target.value;
    if (raw === '' || raw === '-') { setEntry({ offset: raw }); return; }
    const n = parseInt(raw, 10);
    if (!isNaN(n)) setEntry({ offset: n });
  };

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        הגדרת זמן כניסה גלובלי
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2.5, lineHeight: 1.65 }}>
        קבע כמה דקות לפני השקיעה מדליקים נרות בקהילתך — הגדרה זו משמשת כ<strong>ברירת מחדל</strong> לזמן א׳
        של כל ימי המועד. בשלב הבא תוכל לבחור לכל יום בנפרד זמן אחר לחלוטין אם תרצה.
      </Typography>

      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5 }}>
        {entry.key === 'sunset' ? (
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
              <Typography sx={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>
                כמה דקות לפני השקיעה?
              </Typography>
              <TextField
                size="small"
                value={entry.minsBeforeSunset}
                onChange={handleMins}
                inputProps={{ style: { textAlign: 'center', fontSize: '1.1rem', fontWeight: 700, width: 36 } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>דקות</Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 100, ...fieldSx }}
              />
              <Typography sx={{ fontSize: '0.78rem', color: '#9ca3af' }}>
                (בישראל מקובל 20–40 דקות)
              </Typography>
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography
                onClick={() => setEntry({ key: 'plagHamincha', offset: 0 })}
                sx={{ fontSize: '0.73rem', color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}
              >
                השתמש בפלג המנחה במקום →
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography sx={{ fontSize: '0.85rem', color: '#374151', fontWeight: 600 }}>
              פלג המנחה
            </Typography>
            <TextField size="small" value={entry.offset ?? 0} onChange={handlePlagOffset}
              inputProps={{ style: { textAlign: 'center', fontSize: '0.85rem', width: 28 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>דק׳</Typography>
                  </InputAdornment>
                ),
              }}
              sx={{ width: 80, ...fieldSx }}
            />
            <Box sx={{ flex: 1 }} />
            <Typography
              onClick={() => setEntry({ key: 'sunset', minsBeforeSunset: 30 })}
              sx={{ fontSize: '0.73rem', color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}
            >
              ← חזור לשקיעה
            </Typography>
          </Stack>
        )}
      </Box>

      <Box sx={{ bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 2, px: 2, py: 1.25, mt: 2 }}>
        <Typography sx={{ fontSize: '0.75rem', color: '#92400e', lineHeight: 1.6 }}>
          <strong>זמן יציאה גלובלי</strong> — ברירת המחדל היא צאת כוכבים 8.5°. ניתן לשנות לכל יום בנפרד בשלב הבא.
          שקיעה וצאת כוכבים נשאבים מ<strong>ערב יום המועד</strong> (היום שלפני כל חג).
        </Typography>
      </Box>
    </Box>
  );
};

export default MoadCalcStep;
